import { reactLocalStorage } from "reactjs-localstorage";
import _ from 'lodash';
import { CBCAPPROVALTYPEID, CBCTypeId, dropdownLimit, NCCAPPROVALTYPEID, NCCTypeId, NFRAPPROVALTYPEID, NFRTypeId, RELEASESTRATEGYTYPEID1, RELEASESTRATEGYTYPEID2, RELEASESTRATEGYTYPEID3, RELEASESTRATEGYTYPEID4, VBCAPPROVALTYPEID, VBCTypeId, WACAPPROVALTYPEID, WACTypeId, ZBCAPPROVALTYPEID, ZBCTypeId, PFS2APPROVALTYPEID, PFS2TypeId, RELEASE_STRATEGY_B1, RELEASE_STRATEGY_B1_NEW, RELEASE_STRATEGY_B2, RELEASE_STRATEGY_B2_NEW, RELEASE_STRATEGY_B3, RELEASE_STRATEGY_B3_NEW, RELEASE_STRATEGY_B4, RELEASE_STRATEGY_B6, RELEASE_STRATEGY_B6_NEW, RELEASE_STRATEGY_B4_NEW, RELEASESTRATEGYTYPEID6, LPSAPPROVALTYPEID, CLASSIFICATIONAPPROVALTYPEID, RAWMATERIALAPPROVALTYPEID, effectiveDateRangeDays, searchCount, ZBC, VBC, RFQAPPROVALTYPEID, COMPONENTASSEMBLY_APPROVALID, RAWMATERIAL_APPROVALID, BOUGHTOUTPART_APPROVALID, TOOLING_APPROVALID, COSTING_HEAD_TYPES } from "../../config/constants";
import Toaster from "./Toaster";
import { addDays,subDays } from "date-fns";
import { checkForDecimalAndNull, checkForNull, getConfigurationKey, preciseAdd, preciseMultiply, preciseDivide, isExchangeRateBasedOnSettlementFrequencyForDomesticMaster, isExchangeRateBasedOnSettlementFrequencyForImportMaster } from "../../helper";
import DayTime from "./DayTimeWrapper";

/**
 * Validates revised rate values for file uploads
 * @param {any} value - Value to validate
 * @param {string} fieldName - Field name for error message
 * @param {number} rowNum - Row number (optional)
 * @param {number} basicRate - Basic rate for scrap rate comparison (optional)
 * @param {number} revisedBasicRate - Revised basic rate for scrap rate comparison (optional)
 * @param {number} existingScrapRate - Existing scrap rate to allow 0 if existing is also 0 (optional)
 * @returns {object} - { isValid: boolean, error: string }
 */
export const validateRevisedRate = (value, fieldName = 'Rate', rowNum = null, basicRate = null, revisedBasicRate = null, existingScrapRate = null) => {
    // Empty values are valid (optional fields)
    if (value === '' || value === null || value === undefined) {
        return { isValid: true };
    }
    
    const config = getConfigurationKey() || {};
    const strValue = String(value).trim();
    const rowInfo = rowNum ? ` at row ${rowNum}` : '';
    
    // Check if numeric
    if (!/^-?\d*\.?\d+$/.test(strValue)) {
        return { 
            isValid: false, 
            error: `${fieldName}${rowInfo} must be numeric. Found: "${strValue}"` 
        };
    }
    
    const numValue = Number(strValue);
    
    // Special handling for scrap rates
    const isScrapRate = fieldName.toLowerCase().includes('scrap');
    const isRevisedScrapRate = isScrapRate && fieldName.toLowerCase().includes('revised');
    
    // Check negative/zero
    if (numValue < 0) {
        return { 
            isValid: false, 
            error: `${fieldName}${rowInfo} cannot be negative. Found: ${numValue}` 
        };
    }
    
    // For scrap rate (not revised), allow 0 if existing scrap rate is also 0
    if (numValue === 0) {
        if (isScrapRate && !isRevisedScrapRate && existingScrapRate === 0) {
            // Allow 0 for scrap rate if existing is also 0
            return { isValid: true };
        }
        // For all other cases (including revised scrap rate), 0 is not allowed
        return { 
            isValid: false, 
            error: `${fieldName}${rowInfo} must be positive. Found: ${numValue}` 
        };
    }

    // Use NoOfDecimalForPrice for digits before decimal
    const maxDigitsBeforeDecimal = 8;
    const maxDigitsAfterDecimal = config.NoOfDecimalForPrice; // Fixed limit for decimal places
    
    // Split the number into integer and decimal parts
    const parts = strValue.split('.');
    const integerPart = parts[0].replace(/^-/, ''); // Remove minus sign if present
    const decimalPart = parts[1] || '';
    
    // Check digits before decimal
    if (integerPart.length > maxDigitsBeforeDecimal) {
        return { 
            isValid: false, 
            error: `${fieldName}${rowInfo} cannot have more than ${maxDigitsBeforeDecimal} digits before decimal. Found: ${integerPart.length} digits in value: ${strValue}` 
        };
    }
    
    // Check digits after decimal - revised rates allow max 4 decimal places
    if (decimalPart.length > maxDigitsAfterDecimal) {
        return { 
            isValid: false, 
            error: `${fieldName}${rowInfo} max ${maxDigitsAfterDecimal} decimal places allowed. Found: ${decimalPart.length} decimal places in value: ${strValue}` 
        };
    }
    
    // Special checks for different types of revised rates
    // isScrapRate already declared above
    const isRevisedBasicRate = fieldName.toLowerCase().includes('revised') && fieldName.toLowerCase().includes('basic');
    
    // For revised scrap rate - must not exceed revised basic rate or basic rate
    if (isScrapRate) {
        // Determine which rate to compare against
        let compareRate = null;
        let compareRateLabel = '';
        
        // If revised basic rate exists, use it; otherwise use basic rate
        if (revisedBasicRate !== null && revisedBasicRate !== undefined && revisedBasicRate !== '' && revisedBasicRate !== '0') {
            compareRate = Number(revisedBasicRate);
            compareRateLabel = 'Revised Basic Rate';
        } else if (basicRate !== null && basicRate !== undefined && basicRate !== '' && basicRate !== '0') {
            // No revised basic rate, so compare with existing basic rate
            compareRate = Number(basicRate);
            compareRateLabel = 'Basic Rate';
        }
        
        // If we have a rate to compare against, validate
        if (compareRate !== null && numValue > compareRate) {
            return { 
                isValid: false, 
                error: `${fieldName}${rowInfo} must be less than or equal to ${compareRateLabel}. ${fieldName}: ${numValue}, ${compareRateLabel}: ${compareRate}` 
            };
        }
    }
    
    // For revised basic rate - can optionally check against basic rate if needed
    if (isRevisedBasicRate && basicRate !== null && basicRate !== undefined && basicRate !== '') {
        // This check is optional - uncomment if you want revised basic rate to not exceed basic rate
        // const basicRateNum = Number(basicRate);
        // if (numValue > basicRateNum) {
        //     return { 
        //         isValid: false, 
        //         error: `${fieldName}${rowInfo} cannot exceed Basic Rate. ${fieldName}: ${numValue}, Basic Rate: ${basicRateNum}` 
        //     };
        // }
    }
    
    return { isValid: true };
};

/**
 * Validates multiple rates and shows errors
 * @param {Array} items - Array of {value, fieldName, rowNum, basicRate, revisedBasicRate, existingScrapRate}
 * @param {boolean} showToaster - Show toaster notifications
 * @returns {object} - {isValid: boolean, errors: Array}
 */
export const validateRatesInBatch = (items, showToaster = true) => {
    const errors = [];
    
    items.forEach(item => {
        const result = validateRevisedRate(
            item.value, 
            item.fieldName, 
            item.rowNum,
            item.basicRate, // Pass basic rate for scrap rate comparison
            item.revisedBasicRate, // Pass revised basic rate for priority comparison
            item.existingScrapRate // Pass existing scrap rate for 0 value validation
        );
        if (!result.isValid) {
            errors.push(result.error);
        }
    });
    
    if (showToaster && errors.length > 0) {
        // Show summary for many errors, individual for few
        if (errors.length > 3) {
            Toaster.warning(`Found ${errors.length} validation errors. Check console for details.`);
            errors.forEach(err => console.warn(err));
        } else {
            errors.forEach((err, i) => {
                setTimeout(() => Toaster.warning(err), i * 200);
            });
        }
    }
    
    return { 
        isValid: errors.length === 0, 
        errors 
    };
};
// COMMON FILTER FUNCTION FOR AUTOCOMPLETE DROPDOWN
const commonFilterFunction = (inputValue, dropdownArray, filterByName, selectedParts = false) => {
    let tempArr = []
    tempArr = _.filter(dropdownArray, i => {
        return i[filterByName]?.toLowerCase().includes(inputValue?.toLowerCase())
    });
    if (selectedParts) {
        let temp = []
        tempArr && tempArr.map(item => {
            if (selectedParts.includes(item.value)) return false
            temp.push(item)
        })
        return temp
    } else {
        return tempArr
    }
}
const commonDropdownFunction = (array, tempBoolean = false, selectedParts = [], finalArray, partWithRev = false, isRMBOPPartCombine = false) => {
    array && array.map(item => {
        if (item.Value === '0' || item.PartId === '0' || item.Id === '0') return array
        if ((tempBoolean && (item?.PartId || item?.Id) && selectedParts.includes(item.PartId || item.Id)) || (tempBoolean && (item?.Value) && selectedParts.includes(item.Value))) return false        //FOR REMOVING DUPLICATE PART ENTRY         
        if (partWithRev) {
            if (isRMBOPPartCombine) {
                finalArray.push({ label: `${item?.Number}`, value: item?.Id })
            } else {
                finalArray.push({ label: `${item?.PartNumber}${item?.RevisionNumber ? ` (${item?.RevisionNumber})` : ''}`, value: item?.PartId, RevisionNumber: item?.RevisionNumber })
            }
        } else {
            finalArray.push({ label: item.Text, value: item.Value })
        }
        return null
    })
}
// ... existing code ...

export const DropDownFilterList = async (inputValue, filterType, stateKey, apiFunction, setState, state) => {
    if (inputValue && typeof inputValue === 'string' && inputValue.includes(' ')) {
        inputValue = inputValue.trim();
    }
    const resultInput = inputValue.slice(0, searchCount);

    const isNewSearch = !state[stateKey] ||
        (typeof state[stateKey] === 'string' && state[stateKey] !== resultInput) ||
        (typeof state[stateKey] === 'object' && state[stateKey].value !== resultInput);

    if (inputValue?.length >= searchCount && isNewSearch) {
        setState(prevState => ({ ...prevState, inputLoader: true }));
        let res;
        try {
            res = await apiFunction(filterType, resultInput);
            setState(prevState => ({
                ...prevState,
                inputLoader: false,
                [stateKey]: { value: resultInput, label: resultInput }  // Store as an object
            }));
            let dataAPI = res?.data?.SelectList;
            if (inputValue) {
                return autoCompleteDropdown(inputValue, dataAPI, false, [], true);
            } else {
                return dataAPI;
            }
        } catch (error) {
            setState(prevState => ({ ...prevState, inputLoader: false }));
            return [];
        }
    } else {
        if (inputValue?.length < searchCount) return [];
        else {
            let Data = reactLocalStorage?.getObject('Data') || [];
            if (inputValue) {
                return autoCompleteDropdown(inputValue, Data, false, [], false);
            } else {
                return Data;
            }
        }
    }
};

// ... existing code ...

// FOR AUTOCOMPLLETE IN PART AND VENDOR 
export const autoCompleteDropdown = (inputValue, dropdownArray, tempBoolean = false, selectedParts = [], isApiCall) => {
    let tempArr = []
    let finalArr = []
    let finalArr1 = []
    let finalArrCity = []
    if (isApiCall) {
        tempArr = commonFilterFunction(inputValue, dropdownArray, 'Text')
        commonDropdownFunction(tempArr, tempBoolean, selectedParts, finalArr1, false)         //TO SHOW THE FILTERED VENDOR OR PART
        commonDropdownFunction(dropdownArray, tempBoolean, selectedParts, finalArr, false)   // TO STORE ALL VENDOR OR PART DATA IN LOCAL STORAGE
        commonDropdownFunction(dropdownArray, tempBoolean, selectedParts, finalArrCity, false)   // TO STORE ALL cityDATA IN LOCAL STORAGE
        reactLocalStorage?.setObject('Data', finalArr)
        if (finalArr1?.length <= 100) {
            return finalArr1
        } else {
            return _.slice(finalArr1, 0, dropdownLimit)
        }
    }
    else {
        tempArr = commonFilterFunction(inputValue, dropdownArray, "label", selectedParts)
        if (dropdownArray?.length <= 100) {
            return tempArr
        } else {
            return _.slice(tempArr, 0, dropdownLimit)
        }
    }
}

// VOLUME MASTER AUTOCOMPLETE PART 
export const autoCompleteDropdownPart = (inputValue, dropdownArray, tempBoolean = false, selectedParts = [], isApiCall, isRMBOPPartCombine = false) => {
    let tempArr = []
    let finalArr1 = []
    let finalArr = []

    if (isApiCall) {
        tempArr = commonFilterFunction(inputValue, dropdownArray, isRMBOPPartCombine ? "Number" : "PartNumber")
        commonDropdownFunction(tempArr, tempBoolean, selectedParts, finalArr1, true, isRMBOPPartCombine)              //TO SHOW THE FILTERED PART
        commonDropdownFunction(dropdownArray, tempBoolean, selectedParts, finalArr, true, isRMBOPPartCombine)        // TO STORE ALL PART DATA IN LOCAL STORAGE
        reactLocalStorage?.setObject('PartData', finalArr)
        if (finalArr1?.length <= 100) {
            return finalArr1
        } else {
            return _.slice(finalArr1, 0, dropdownLimit)
        }

    }
    else {
        tempArr = commonFilterFunction(inputValue, dropdownArray, "label")
        if (dropdownArray?.length <= 100) {
            return tempArr
        } else {
            return _.slice(tempArr, 0, dropdownLimit)
        }
    }
}
//FUNCTION FOR HIDING CUSTOMER COLUMN FROM LISTING 
export const hideCustomerFromExcel = (data, value) => {
    let excelData
    if (!reactLocalStorage.getObject('CostingTypePermission')?.cbc) {
        excelData = data && data.filter((item) => item.value !== value)
    }
    else {
        excelData = [...data]
    }
    return excelData
}

export const hideColumnFromExcel = (data, value) => {
    let excelData
    excelData = data && data.filter((item) => item.value !== value)
    return excelData
}

export const hideMultipleColumnFromExcel = (data, value) => {
    let excelData
    excelData = data && data.filter((item) => !value?.includes(item.value))
    return excelData
}

export const costingTypeIdToApprovalTypeIdFunction = (value) => {
    let approvalTypeId;
    switch (Number(value)) {
        case ZBCTypeId:
            approvalTypeId = ZBCAPPROVALTYPEID;
            break;
        case VBCTypeId:
            approvalTypeId = VBCAPPROVALTYPEID;
          break;
        case CBCTypeId:
            approvalTypeId = CBCAPPROVALTYPEID;
            break;
        case NCCTypeId:
            approvalTypeId = NCCAPPROVALTYPEID;
            break;
        case NFRTypeId:
            approvalTypeId = NFRAPPROVALTYPEID;
            break;
        case WACTypeId:
            approvalTypeId = WACAPPROVALTYPEID;
            break;
        case RELEASESTRATEGYTYPEID1:
            approvalTypeId = RELEASESTRATEGYTYPEID1;
            break;
        case RELEASESTRATEGYTYPEID2:
            approvalTypeId = RELEASESTRATEGYTYPEID2;
            break;
        case RELEASESTRATEGYTYPEID3:
            approvalTypeId = RELEASESTRATEGYTYPEID3;
            break;
        case RELEASESTRATEGYTYPEID4:
            approvalTypeId = RELEASESTRATEGYTYPEID4;
            break;
        case RELEASESTRATEGYTYPEID6:
            approvalTypeId = RELEASESTRATEGYTYPEID6;
            break;
        case PFS2TypeId:
            approvalTypeId = PFS2APPROVALTYPEID;
            break;
        case LPSAPPROVALTYPEID:
            approvalTypeId = LPSAPPROVALTYPEID;
            break;
        case CLASSIFICATIONAPPROVALTYPEID:
            approvalTypeId = CLASSIFICATIONAPPROVALTYPEID;
            break;
        case RAWMATERIALAPPROVALTYPEID:
            approvalTypeId = RAWMATERIALAPPROVALTYPEID;
            break;
        case RFQAPPROVALTYPEID:
            approvalTypeId = RFQAPPROVALTYPEID;
            break;
        default:
            approvalTypeId = null; // or any default value you prefer
            break;
    }
    return isNaN(approvalTypeId) ? null : approvalTypeId
};
/**
* Transforms an approval item based on its text value.
* @param {Object} item - The approval item with properties like Text and Value.
* @returns {string} - The transformed label for the approval item.
*/
export const transformApprovalItem = (item) => {
    // Switch statement to determine the transformation based on item.Text
    switch (item.Text) {
        case RELEASE_STRATEGY_B1:
            return RELEASE_STRATEGY_B1_NEW;
        case RELEASE_STRATEGY_B2:
            return RELEASE_STRATEGY_B2_NEW;
        case RELEASE_STRATEGY_B3:
            return RELEASE_STRATEGY_B3_NEW;
        case RELEASE_STRATEGY_B4:
            return RELEASE_STRATEGY_B4_NEW;
        case RELEASE_STRATEGY_B6:
            return RELEASE_STRATEGY_B6_NEW;
        default:
            return item.Text; // Return the original text if no transformation is needed
    }
};

export const getCostingTypeIdByCostingPermission = () => {
    const { zbc, vbc, cbc } = reactLocalStorage.getObject('CostingTypePermission') || {};
    const configurationKey = reactLocalStorage.getObject('InitialConfiguration') || {};
    // Handle NaN case - if TypeId is NaN, return null to avoid blank page
    const zbcId = !isNaN(ZBCTypeId) ? ZBCTypeId : null;
    const vbcId = !isNaN(VBCTypeId) ? VBCTypeId : null;
    const cbcId = !isNaN(CBCTypeId) ? CBCTypeId : null;

    // Check if VBC should be default based on configuration
    if (configurationKey?.DefaultCostingTypeForSelectedRadioButton === COSTING_HEAD_TYPES.VBC && vbc) {
        return vbcId;
    }

    // Check if ZBC should be default based on configuration
    if (configurationKey?.DefaultCostingTypeForSelectedRadioButton === COSTING_HEAD_TYPES.ZBC && zbc) {
        return zbcId;
    }
    // Check if CBC should be default based on configuration
    if (configurationKey?.DefaultCostingTypeForSelectedRadioButton === COSTING_HEAD_TYPES.CBC && cbc) {
        return cbcId;
    }
    // Original logic - ZBC first priority, then VBC, then CBC
    const costingTypeId = zbc ? zbcId : vbc ? vbcId : cbc ? cbcId : null;
    return costingTypeId;
}

// Helper function to safely compare costing type IDs (handles NaN)
export const isCostingTypeId = (value, typeId) => {
    if (isNaN(value) || isNaN(typeId)) return false;
    return value === typeId;
}

// Helper function to safely get CostingTypePermission with NaN handling
export const getCostingTypePermission = () => {
    const permission = reactLocalStorage.getObject('CostingTypePermission') || {};
    return {
        zbc: (!isNaN(permission.zbc) ? Boolean(permission.zbc) : false) && !isNaN(ZBCTypeId),
        vbc: (!isNaN(permission.vbc) ? Boolean(permission.vbc) : false) && !isNaN(VBCTypeId),
        cbc: (!isNaN(permission.cbc) ? Boolean(permission.cbc) : false) && !isNaN(CBCTypeId)
    };
};

export const checkMasterCreateByCostingPermission = (isBulkupload = false) => {
    const costingPermision = reactLocalStorage.getObject('CostingTypePermission');
    let count = 0;
    for (const key in costingPermision) {
        if (costingPermision.hasOwnProperty(key)) {
            const value = costingPermision[key];
            if (value) {
                count = count + 1
            }
        }
    }
    if (count === 0) {
        Toaster.warning(`You have not enough permission for ${isBulkupload ? 'uploading' : 'creating'} this master.`)
        return false;
    }
    return true;
}


export const OperationFileData = (fileData) => {
    fileData.forEach(obj => {
        switch (obj.ForType) {
            case 'Welding':
                obj.MaterialWireCRMHead = obj['MaterialWireCRMHead/NiRateCrmHead'];
                obj.MaterialWireRate = obj['MaterialWireRate/NiRate']
                obj.MaterialWireConsumption = obj['MaterialWireConsumption/NiRateConsumption']
                obj.MaterialGasCRMHead = obj['MaterialGasCRMHead/NiScrapCrmHead']
                obj.MaterialGasRate = obj['MaterialGasRate/NiScrapRate']
                obj.MaterialGasConsumption = obj['MaterialGasConsumption/NiScrapRateConsumption']
                obj.PowerElectricityRate = obj['PowerElectricityRate/PowerElectricityCost'];
                delete obj['PowerElectricityRate/PowerElectricityCost'];
                delete obj['MaterialWireCRMHead/NiRateCrmHead'];
                delete obj['MaterialWireRate/NiRate']
                delete obj['MaterialWireConsumption/NiRateConsumption']
                delete obj['MaterialGasCRMHead/NiScrapCrmHead'];
                delete obj['MaterialGasRate/NiScrapRate'];
                delete obj['MaterialGasConsumption/NiScrapRateConsumption'];
                break;
            case 'Ni Cr Plating':
                obj.NiOfficeExpCrmHead = obj['ConsumableCRMHead/NiOfficeExpCrmHead'];
                obj.NiOfficeExp = obj['ConsumableCost/NiOfficeExp'];
                obj.AdditionalChemicalCostCrmHead = obj['ConsumableWaterCRMHead/AdditionalChemicalCostCrmHead'];
                obj.AdditionalChemicalCost = obj['ConsumableWaterCost/AdditionalChemicalCost'];
                obj.CETPChargeCrmHead = obj['ConsumableJigStrippingCRMHead/CETPChargeCrmHead'];
                obj.CETPCharge = obj['ConsumableJigStrippingCost/CETPCharge'];
                obj.FixedCostCrmHead = obj['StatuatoryAndLicenseCRMHead/FixedCostCrmHead'];
                obj.FixedCost = obj['StatuatoryAndLicenseCost/FixedCost'];
                obj.NiRateCrmHead = obj['MaterialWireCRMHead/NiRateCrmHead'];
                obj.NiRate = obj['MaterialWireRate/NiRate']
                obj.NiRateConsumption = obj['MaterialWireConsumption/NiRateConsumption']
                obj.NiScrapCrmHead = obj['MaterialGasCRMHead/NiScrapCrmHead']
                obj.NiScrapRate = obj['MaterialGasRate/NiScrapRate']
                obj.NiScrapRateConsumption = obj['MaterialGasConsumption/NiScrapRateConsumption']
                obj.PowerElectricityRate = obj['PowerElectricityRate/PowerElectricityCost'];
                delete obj['PowerElectricityRate/PowerElectricityCost'];
                delete obj['ConsumableCRMHead/NiOfficeExpCrmHead'];
                delete obj['ConsumableCost/NiOfficeExp'];
                delete obj['ConsumableWaterCRMHead/AdditionalChemicalCostCrmHead'];
                delete obj['ConsumableWaterCost/AdditionalChemicalCost'];
                delete obj['ConsumableJigStrippingCRMHead/CETPChargeCrmHead'];
                delete obj['ConsumableJigStrippingCost/CETPCharge'];
                delete obj['StatuatoryAndLicenseCRMHead/FixedCostCrmHead'];
                delete obj['StatuatoryAndLicenseCost/FixedCost'];
                delete obj['MaterialWireCRMHead/NiRateCrmHead'];
                delete obj['MaterialWireRate/NiRate']
                delete obj['MaterialWireConsumption/NiRateConsumption']
                delete obj['MaterialGasCRMHead/NiScrapCrmHead'];
                delete obj['MaterialGasRate/NiScrapRate'];
                delete obj['MaterialGasConsumption/NiScrapRateConsumption'];
                break;
            case 'Surface Treatment':
            case 'Other Operation':
                obj.ConsumableCRMHead = obj['ConsumableCRMHead/NiOfficeExpCrmHead'];
                obj.ConsumableCost = obj['ConsumableCost/NiOfficeExp'];
                obj.ConsumableWaterCRMHead = obj['ConsumableWaterCRMHead/AdditionalChemicalCostCrmHead'];
                obj.ConsumableWaterCost = obj['ConsumableWaterCost/AdditionalChemicalCost'];
                obj.ConsumableJigStrippingCRMHead = obj['ConsumableJigStrippingCRMHead/CETPChargeCrmHead'];
                obj.ConsumableJigStrippingCost = obj['ConsumableJigStrippingCost/CETPCharge'];
                obj.StatuatoryAndLicenseCRMHead = obj['StatuatoryAndLicenseCRMHead/FixedCostCrmHead'];
                obj.StatuatoryAndLicenseCost = obj['StatuatoryAndLicenseCost/FixedCost'];
                obj.PowerElectricityCost = obj['PowerElectricityRate/PowerElectricityCost'];
                delete obj['ConsumableCRMHead/NiOfficeExpCrmHead'];
                delete obj['ConsumableCost/NiOfficeExp'];
                delete obj['ConsumableWaterCRMHead/AdditionalChemicalCostCrmHead'];
                delete obj['ConsumableWaterCost/AdditionalChemicalCost'];
                delete obj['ConsumableJigStrippingCRMHead/CETPChargeCrmHead'];
                delete obj['ConsumableJigStrippingCost/CETPCharge'];
                delete obj['StatuatoryAndLicenseCRMHead/FixedCostCrmHead'];
                delete obj['StatuatoryAndLicenseCost/FixedCost'];
                delete obj['PowerElectricityRate/PowerElectricityCost'];
                break;
            default:
                break;
        }
    });
    return fileData;
}
export const getRMCostIds = () => {
    let costIds = reactLocalStorage.getObject('InitialConfiguration').CostingConditionTypes
    return costIds
}
export const getCostingConditionTypes = (conditionName) => {
    let arr = getRMCostIds()
    let costingTypeId = arr && arr?.filter(item => item['CostingConditionTypeName'] === conditionName)
    return costingTypeId[0]?.CostingConditionTypeMasterId
}


export const getEffectiveDateMinDate = (minDate) => {

    if (effectiveDateRangeDays === null) {
        return new Date(new Date().getFullYear() - 100, 0, 1); // Allow dates up to 100 years in the past
    }
    if (effectiveDateRangeDays === 0) {
        return new Date(); // No past dates
    }
    return subDays(new Date(), effectiveDateRangeDays);
};

export const getEffectiveDateMaxDate = () => {
    const effectiveDateRangeDayFuture = reactLocalStorage.getObject('InitialConfiguration')?.EffectiveDateRangeDayFuture;

    if (effectiveDateRangeDayFuture === null) {
        return new Date(new Date().getFullYear() + 100, 11, 31); // Allow dates up to 100 years in the future
    }
    if (effectiveDateRangeDayFuture === 0) {
        return new Date(); // No future dates
    }

    return addDays(new Date(), effectiveDateRangeDayFuture);
};

export const convertIntoCurrency = (price, currencyValue) => {
    // Use higher precision for exchange rate calculations (6 decimal places)
    const result = checkForNull(price) * checkForNull(currencyValue ?? 1);
    // Return with appropriate precision - don't round here, let display formatters handle it
    return Number(result.toFixed(10)); // Use 10 decimal places internally to maintain precision
}


export const generateCombinations = (arr, rate) => {
    const result = [];

    // Helper function to generate combinations
    const generateCombos = (start, current) => {
        // Add the current combination to the result
        if (current.length > 0) {
            result.push({ label: current.join(" + "), value: result.length });
        }

        // Generate further combinations
        for (let i = start; i < arr.length; i++) {
            generateCombos(i + 1, [...current, arr[i]]);
        }
    };

    // Start the combination generation
    generateCombos(0, []);

    // Sort the result to ensure "Basic Rate" is always first in combinations
    result.sort((a, b) => {
        if (a.label.includes(rate) && !b.label.includes(rate)) return -1;
        if (!a.label.includes(rate) && b.label.includes(rate)) return 1;
        return 0;
    });

    return result;
};

/**
 * Handles applicability calculations for costs
 */
export const handleApplicability = (value, basicPriceBaseCurrency, arr, costField, headerName, applicabilityName) => {
    if (!value) return 0;
    const selectedApplicabilities = value?.split(' + ');
    // Calculate total cost currency for selected applicabilities
    const total = selectedApplicabilities.reduce((acc, applicability) => {
        const trimmedApplicability = applicability.trim();

        // If applicability is "Basic Rate", return basic price
        if (trimmedApplicability === applicabilityName) {
            return preciseAdd(checkForNull(acc), checkForNull(basicPriceBaseCurrency));
        }

        // Find matching cost item
        const item = arr?.find(item => item?.[headerName]?.trim?.() === trimmedApplicability);
        if (item) {
            return preciseAdd(checkForNull(acc), checkForNull(item?.[costField]));
        }

        return checkForNull(acc);
    }, 0);

    return total;
};

/**
 * Recalculates condition costs
 */
export const recalculateConditions = (basicPriceBaseCurrency, state,isSimulation,nonIndexationSimulation) => {      
    
    
    return recalculateCosts(
        false,
        basicPriceBaseCurrency,
        "ConditionType",
        "Applicability",
        "Percentage",
        { 
            mainCost: "ConditionCost",
            conversionCost: "ConditionCostConversion",
            perQuantityCost: "ConditionCostPerQuantity",
            // mainCost: !nonIndexationSimulation?"ConditionCost":"NewConditionCost",
            // conversionCost: !nonIndexationSimulation?"ConditionCostConversion":"NewConditionNetCostConversion",
            // perQuantityCost: !nonIndexationSimulation?"ConditionCostPerQuantity":"NewConditionNetCostLocalConversion",
        },
        'Description',
        state,
        "Basic Price",
        isSimulation,
        nonIndexationSimulation,
    );  
};

/**
 * Recalculates other costs
 */
export const recalculateOtherCost = (basicRate, state, isSimulation,nonIndexationSimulation) => {
    // Get the recalculated costs
    const recalculatedCosts = recalculateCosts(
        true,
        basicRate,
        "Type",
        "Applicability",
        "Value",
        {
            mainCost: "NetCost",
            conversionCost: "NetCostConversion",
            perQuantityCost: "NetCostConversion",
        },
        'CostHeaderName',
        state,
        "Basic Rate",
        isSimulation,
        nonIndexationSimulation
    );
 
    // Process discount costs to ensure they are always negative
    if (recalculatedCosts && recalculatedCosts.length > 0) {
        recalculatedCosts.forEach(item => {
            // If this is a discount cost, ensure it's always negative
            if (item.CostHeaderName === "Discount Cost") {
                // Ensure NetCost is always negative
                if (item.NetCost !== undefined && item.NetCost !== null) {
                    const netCostValue = checkForNull(item.NetCost);
                    item.NetCost = netCostValue >= 0 ? -Math.abs(netCostValue) : netCostValue;
                }
                
                // Ensure NetCostConversion is always negative
                if (item.NetCostConversion !== undefined && item.NetCostConversion !== null) {
                    const netCostConversionValue = checkForNull(item.NetCostConversion);
                    item.NetCostConversion = netCostConversionValue >= 0 ? -Math.abs(netCostConversionValue) : netCostConversionValue;
                }
            }
        });
    }
 
    return recalculatedCosts;
};


/**
 * Core cost recalculation logic
 */

export const recalculateCosts = (isOtherCost, basicValue, typeField, applicabilityField, percentageField, resultFields, headerName, state, applicabilityName, isSimulation, basicRateValue,nonIndexationSimulation = false) => {
    // Select the correct data array and make a deep copy
    const dataArray = isOtherCost ? state.otherCostTableData : state.conditionTableData;
    
    const costField = isOtherCost ? 'NetCost' : !nonIndexationSimulation?'ConditionCost':'NewConditionCost';
    let copiedData = _.cloneDeep(dataArray) ?? [];
    
    // Create a temporary array for calculations
    let tempArr = copiedData||[];
    // Process each item in the array
    copiedData?.forEach((item, index) => {
        if (item?.[typeField] === "Percentage") {
            // Calculate costs
            const ApplicabilityCost = handleApplicability(
                item[applicabilityField],
                basicValue,
                tempArr,
                costField,
                headerName,
                applicabilityName
            );
            const percentageDecimal = preciseDivide(checkForNull(item?.[percentageField]), 100);
            let Cost = preciseMultiply(checkForNull(percentageDecimal), checkForNull(ApplicabilityCost));
            let CostConversion = preciseMultiply(checkForNull(percentageDecimal), checkForNull(ApplicabilityCost));

            // Special handling for Yield Loss: cost = ApplicabilityCost * (p / (1 - p))
            if (item?.[headerName] === "Yield Loss") {
                const oneMinusP = preciseAdd(1, preciseMultiply(-1, checkForNull(percentageDecimal))); // 1 - p
                // Guard against division by zero or negative denominator
                if (checkForNull(oneMinusP) > 0) {
                    const yieldFactor = preciseDivide(checkForNull(percentageDecimal), checkForNull(oneMinusP)); // p / (1 - p)
                    Cost = preciseMultiply(checkForNull(yieldFactor), checkForNull(ApplicabilityCost));
                    CostConversion = preciseMultiply(checkForNull(yieldFactor), checkForNull(ApplicabilityCost));
                } else {
                    Cost = 0;
                    CostConversion = 0;
                }
            }

            if(item?.[headerName] === "Discount Cost"){
                Cost = -Cost
                CostConversion = -CostConversion
            }
            // Create updated object
            const updatedItem = {
                ...item,
                ApplicabilityCost: ApplicabilityCost,
                [resultFields.mainCost]: Cost,
                [resultFields.conversionCost]: CostConversion,
                [resultFields.perQuantityCost]: CostConversion,
                ApplicabilityCostConversion: ApplicabilityCost,
            };
            // Update the temporary array
            tempArr[index] = updatedItem;
        }
    });
    return tempArr;
};
 
/**
 * Updates cost values and handles state updates
 */
export const updateCostValue = (isConditionCost, state, price, isSimulation = false,nonIndexationSimulation = false) => {
    // Get table data and settings based on cost type
  const table = isConditionCost
        ? recalculateConditions(isSimulation ? price : (nonIndexationSimulation ?state.OldNetCostWithoutConditionCost

            :state.NetCostWithoutConditionCost), state, isSimulation,nonIndexationSimulation)
        : recalculateOtherCost(price, state, isSimulation,nonIndexationSimulation);

    // Calculate sum
    const costField = isConditionCost ? 'ConditionCostPerQuantity' : 'NetCost';
    let sum = 0;

    if (table && table.length > 0) {
        sum = table.reduce((acc, obj) => {
            const costValue = checkForNull(obj[costField]);

            // For discount costs, ensure they are always treated as negative
            if (!isConditionCost && obj.CostHeaderName === "Discount Cost") {
                // If the value is positive, make it negative; if already negative, keep as is
                const discountValue = costValue >= 0 ? -Math.abs(costValue) : costValue;
                return preciseAdd(checkForNull(acc), discountValue);
            }

            return preciseAdd(checkForNull(acc), costValue);
        }, 0);
    }

    // Return updated state and form values
    return {
        updatedState: {
            ...state,
            [isConditionCost ? 'NetConditionCost' : 'totalOtherCost']: sum,
            [isConditionCost ? 'conditionTableData' : 'otherCostTableData']: table
        },
        formValue: {
            field: isConditionCost ? 'FinalConditionCost' : 'OtherCost',
            value: checkForDecimalAndNull(sum, getConfigurationKey().NoOfDecimalForPrice)
        },
        tableData: table
    };
};
export const checkEffectiveDate = (effectiveDate,effectiveDateToChange) => {
    return DayTime(effectiveDate).format('YYYY-MM-DD HH:mm:ss') === DayTime(effectiveDateToChange).format('YYYY-MM-DD HH:mm:ss')
}

export const compareRateCommon = (otherCostData, conditionCostData) => {
    if (otherCostData?.[0]?.Applicability === "Basic Rate" && conditionCostData?.[0]?.Applicability === "Basic Price") {
        Toaster.warning("Please click on refresh button to update Other Cost and Condition Cost data.");
    } else if (otherCostData?.[0]?.Applicability === "Basic Rate") {
        Toaster.warning("Please click on refresh button to update Other Cost data.");
    } else if (conditionCostData?.[0]?.Applicability === "Basic Price") {
        Toaster.warning("Please click on refresh button to update Condition Cost data.");
    }
};

export const parsedDate=(EffectiveDate)=>{
    if (EffectiveDate) {
        const [day, month, year] = EffectiveDate.split('/');
        const parsedDate = new Date(year, month - 1, day);
        return parsedDate;
    } else {
        return null;
    }
}

/**
 * Get Technology ID based on Part Type
 * This function handles all possible input formats for part types and returns the corresponding Technology ID
 *
 * @param {string} partType - Part type in any format:
 *   - Component/Assembly formats: 'componentAssembly', 'Component/Assembly', 'Component', 'Assembly', 'COMP/ASSY'
 *   - Raw Material formats: 'RawMaterial', 'Raw Material', 'RM'
 *   - Bought Out Part formats: 'BoughtOutPart', 'Bought Out Part', 'BOP'
 *   - Tooling formats: 'Tooling'
 *
 * @returns {string} Technology ID:
 *   - '1' for Component/Assembly
 *   - '2' for Raw Material
 *   - '3' for Bought Out Part
 *   - '4' for Tooling
 *   - '1' as default fallback
 *
 * @example
 * getTechnologyIdByPartType('Raw Material') // returns '2'
 * getTechnologyIdByPartType('componentAssembly') // returns '1'
 * getTechnologyIdByPartType('BOP') // returns '3'
 */
export const getRFQIdByPartType = (partType) => {
    // Return default if no partType provided
    if (!partType) {
        return COMPONENTASSEMBLY_APPROVALID || reactLocalStorage.get('componentAssemblyApprovalId') || '1';
    }

    // Normalize input: convert to lowercase and remove extra spaces
    const normalized = String(partType).trim().toLowerCase();

    // Component/Assembly - ID: 1
    // Matches: 'componentAssembly', 'Component/Assembly', 'Component', 'Assembly', 'COMP/ASSY', 'component', 'assembly'
    if (
        normalized === 'componentassembly' ||
        normalized === 'component/assembly' ||
        normalized === 'component' ||
        normalized === 'assembly' ||
        normalized === 'comp/assy' ||
        normalized.includes('component') ||
        normalized.includes('assembly')
    ) {
        return COMPONENTASSEMBLY_APPROVALID || reactLocalStorage.get('componentAssemblyApprovalId') || '1';
    }

    // Raw Material - ID: 2
    // Matches: 'RawMaterial', 'Raw Material', 'RM', 'raw material', 'rawmaterial'
    if (
        normalized === 'rawmaterial' ||
        normalized === 'raw material' ||
        normalized === 'rm' ||
        normalized.includes('raw material') ||
        normalized.includes('rawmaterial')
    ) {
        return RAWMATERIAL_APPROVALID || reactLocalStorage.get('rawMaterialApprovalId') || '2';
    }

    // Bought Out Part - ID: 3
    // Matches: 'BoughtOutPart', 'Bought Out Part', 'BOP', 'bought out part', 'boughtoutpart'
    if (
        normalized === 'boughtoutpart' ||
        normalized === 'bought out part' ||
        normalized === 'bop' ||
        normalized.includes('bought out part') ||
        normalized.includes('boughtoutpart')
    ) {
        return BOUGHTOUTPART_APPROVALID || reactLocalStorage.get('boughtOutPartApprovalId') || '3';
    }

    // Tooling - ID: 4
    // Matches: 'Tooling', 'tooling', 'TOOLING'
    if (normalized === 'tooling') {
        return TOOLING_APPROVALID || reactLocalStorage.get('toolingApprovalId') || '4';
    }

    // Default fallback to Component/Assembly
    return COMPONENTASSEMBLY_APPROVALID || reactLocalStorage.get('componentAssemblyApprovalId') || '1';
};


export const getDefaultTechnologyForTechnologyDropdown = () => {
    const configurationKey = reactLocalStorage.getObject('InitialConfiguration') || {};
    const technology = configurationKey?.DefaultTechnologyForTechnologyDropdown?.[0];
    if (!technology) return '';
    return {
        label: technology?.TechnologyName,
        value: technology?.TechnologyId,
    };
}

// For multiselect fields that expect { Value, Text } format in an array
export const getDefaultTechnologyForMultiSelect = () => {
    const configurationKey = reactLocalStorage.getObject('InitialConfiguration') || {};
    const technology = configurationKey?.DefaultTechnologyForTechnologyDropdown?.[0];
    if (!technology) return [];
    return [{
        Text: technology?.TechnologyName,
        Value: technology?.TechnologyId,
    }];
}


